import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, Loader2, RefreshCw, ChevronDown, ChevronRight, ClipboardCheck } from "lucide-react";
import { adminApi } from "../api";

/**
 * Painel de auditoria DTP — mostra que dados faltam para o DTP estar pronto a gerar.
 *
 * Estados por item: "ok" (preenchido), "warn" (opcional em falta), "missing"
 * (obrigatório em falta — bloqueia). Cada grupo mostra contadores e pode ser
 * expandido para ver detalhe. Painel global mostra ready/score.
 */

const STATUS_ICON = {
  ok:      { Icon: CheckCircle2, color: "text-[#3F8B4B]" },
  warn:    { Icon: AlertTriangle, color: "text-[#B87A1A]" },
  missing: { Icon: XCircle,      color: "text-[#B94A48]" },
};

function StatusBadge({ status, label }) {
  const { Icon, color } = STATUS_ICON[status] || STATUS_ICON.warn;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] ${color}`}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

function GlobalHeader({ audit, onRefresh, refreshing }) {
  if (!audit) return null;
  const ready = audit.ready_to_generate;
  const bg = ready ? "bg-[#EAF6EC] border-[#3F8B4B]" : (audit.blocking_count ? "bg-[#FCEBEA] border-[#B94A48]" : "bg-[#FDF3E1] border-[#B87A1A]");
  const label = ready ? "DTP pronto a gerar" : (audit.blocking_count ? `${audit.blocking_count} campo(s) obrigatório(s) em falta` : `${audit.warning_count} avisos opcionais`);
  const Icon = ready ? CheckCircle2 : (audit.blocking_count ? XCircle : AlertTriangle);
  const iconColor = ready ? "text-[#3F8B4B]" : (audit.blocking_count ? "text-[#B94A48]" : "text-[#B87A1A]");
  return (
    <div className={`border-l-4 rounded-md p-3 flex items-center gap-3 ${bg}`} data-testid="dtp-audit-global-header">
      <Icon className={`w-5 h-5 shrink-0 ${iconColor}`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[#1B1B1B]">{label}</div>
        <div className="text-[11px] text-[#6E6754]">
          {audit.ok_count} OK · {audit.warning_count} avisos · {audit.blocking_count} bloqueios · score {audit.score}%
        </div>
      </div>
      <button
        type="button"
        onClick={onRefresh}
        disabled={refreshing}
        className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-white/80 hover:bg-white text-[#6E6754] border border-[#E6E1D6]"
        title="Recorrer a auditoria"
        data-testid="dtp-audit-refresh"
      >
        {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
        Recarregar
      </button>
    </div>
  );
}

function GroupCard({ group, expanded, onToggle }) {
  const { Icon, color } = STATUS_ICON[group.status] || STATUS_ICON.warn;
  const Chevron = expanded ? ChevronDown : ChevronRight;
  const showItems = group.items.filter((i) => i.status !== "ok" || expanded);
  return (
    <div
      className="bg-white border border-[#E6E1D6] rounded-md"
      data-testid={`dtp-audit-group-${group.id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[#F9F7F1] transition text-left"
        data-testid={`dtp-audit-group-${group.id}-toggle`}
      >
        <Chevron className="w-4 h-4 text-[#6E6754] shrink-0" />
        <Icon className={`w-4 h-4 shrink-0 ${color}`} />
        <span className="text-sm font-medium text-[#1B1B1B] flex-1 min-w-0 truncate">{group.label}</span>
        <div className="flex items-center gap-2 shrink-0 text-[10px]">
          {group.counts.missing > 0 && <span className="text-[#B94A48] font-semibold">{group.counts.missing} bloq</span>}
          {group.counts.warn > 0 && <span className="text-[#B87A1A]">{group.counts.warn} avisos</span>}
          <span className="text-[#6E6754]">{group.counts.ok}/{group.counts.total} OK</span>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-[#F3F0E7] px-3 py-2 space-y-1.5" data-testid={`dtp-audit-group-${group.id}-items`}>
          {showItems.length === 0 && (
            <div className="text-[11px] text-[#6E6754] italic">Todos os campos deste grupo estão preenchidos. 🎉</div>
          )}
          {showItems.map((it) => {
            const { Icon: IIcon, color: iColor } = STATUS_ICON[it.status] || STATUS_ICON.warn;
            return (
              <div
                key={it.key}
                className="flex items-start gap-2 text-xs"
                data-testid={`dtp-audit-item-${it.key}`}
              >
                <IIcon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${iColor}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-[#1B1B1B]">{it.label}</div>
                  {it.hint && it.status !== "ok" && (
                    <div className="text-[10px] text-[#6E6754] mt-0.5">{it.hint}</div>
                  )}
                  {it.value && it.status === "ok" && (
                    <div className="text-[10px] text-[#978D78] mt-0.5 truncate italic">&ldquo;{it.value}&rdquo;</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function DtpAuditPanel({ cursoId, autoLoad = true }) {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    if (!cursoId) return;
    setRefreshing(true);
    try {
      const r = await adminApi.cursoDtpAudit(cursoId);
      setAudit(r);
      // Auto-expandir grupos com problemas
      const exp = {};
      (r.groups || []).forEach((g) => { if (g.status !== "ok") exp[g.id] = true; });
      setExpanded(exp);
    } catch (e) {
      /* silent — mostra estado vazio */
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [cursoId]);

  useEffect(() => { if (autoLoad) load(); }, [autoLoad, load]);

  const toggle = (gid) => setExpanded((s) => ({ ...s, [gid]: !s[gid] }));

  return (
    <section
      className="bg-white border border-[#E6E1D6] rounded-md p-4 space-y-3"
      data-testid="dtp-audit-panel"
    >
      <div className="flex items-center gap-2">
        <ClipboardCheck className="w-4 h-4 text-[#A8843C]" />
        <h2 className="text-sm font-semibold text-[#A8843C] uppercase tracking-wider">Auditoria de Dados</h2>
      </div>
      {loading && !audit ? (
        <div className="flex items-center gap-2 text-xs text-[#6E6754]">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> A auditar…
        </div>
      ) : audit ? (
        <>
          <GlobalHeader audit={audit} onRefresh={load} refreshing={refreshing} />
          <div className="space-y-1.5">
            {(audit.groups || []).map((g) => (
              <GroupCard
                key={g.id}
                group={g}
                expanded={!!expanded[g.id]}
                onToggle={() => toggle(g.id)}
              />
            ))}
          </div>
          <div className="flex items-center justify-between pt-1">
            <span className="text-[10px] text-[#978D78]">
              Legenda: <StatusBadge status="ok" label="preenchido" /> · <StatusBadge status="warn" label="opcional em falta" /> · <StatusBadge status="missing" label="obrigatório em falta" />
            </span>
            <button
              type="button"
              onClick={() => setExpanded(Object.fromEntries((audit.groups || []).map((g) => [g.id, true])))}
              className="text-[10px] text-[#A8843C] hover:underline"
              data-testid="dtp-audit-expand-all"
            >
              Expandir tudo
            </button>
          </div>
        </>
      ) : (
        <div className="text-xs text-[#B94A48]">Não foi possível carregar a auditoria.</div>
      )}
    </section>
  );
}
